import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router";
import { LiveKitRoom } from "@livekit/components-react";
import "@livekit/components-styles";
import { Loader2 } from "lucide-react";

import { requestTherapistMeetingToken, sessionForAppointment, type MeetingAccess, meetingJoinFailureFrom, MeetingStage, MeetingError, MeetingHeader, MeetingRoom, MeetingElapsed, MEETING_ROOM_OPTIONS } from "../../../session";
import type { MeetingJoinFailure, MeetingRefusalReason } from "../../../session";

/**
 * An AlchelyMeeting room, as the therapist sees it.
 *
 * The token is fetched rather than passed in: it is short-lived and scoped to
 * one room, so reloading the page is a fresh grant rather than a replay of an
 * old one. The call itself is `MeetingStage`, the same one the session and
 * guest rooms use, so all three speak the therapist's language and behave
 * alike.
 *
 * Marked `translate="no"` for the same reason the other two rooms are: a
 * browser translating a live call rewrites the DOM under React and takes the
 * call down with it.
 */
/**
 * Why a room would not open, in the words the person at the door needs.
 *
 * A reason this client does not know falls back to the wait, which is the
 * answer that costs least when it is wrong: it asks somebody to come back
 * rather than telling them their session is gone.
 */
const ROOM_SHUT: Record<MeetingRefusalReason, string> = {
    notOpenYet: "scheduler.meeting.notOpen",
    ended: "scheduler.meeting.ended",
    cancelled: "scheduler.meeting.cancelled",
    notHostedHere: "scheduler.meeting.notHostedHere",
    unrecognised: "scheduler.meeting.notOpen",
};

/**
 * A room refusing the hour is not the room refusing the person — but not
 * always the same refusal either. "Come back fifteen minutes early" is the
 * wrong answer for a session that ended last Tuesday, and for one that was
 * called off it is the wrong answer twice, so the reason is read rather than
 * guessed from the failure alone.
 */
function failureMessage(failure: MeetingJoinFailure): string {
    switch (failure.kind) {
        case "refused":
            return ROOM_SHUT[failure.refusal.reason];
        case "forbidden":
        case "notFound":
            return "scheduler.meeting.notYours";
        default:
            return "scheduler.meeting.unavailable";
    }
}

export default function MeetingRoomPage() {
    const { t } = useTranslation();
    const { appointmentId } = useParams<{ appointmentId: string }>();
    const navigate = useNavigate();

    const [access, setAccess] = useState<MeetingAccess | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [connected, setConnected] = useState(false);

    useEffect(() => {
        if (!appointmentId) return;
        let cancelled = false;

        const joinBareRoom = () =>
            requestTherapistMeetingToken(appointmentId)
                .then((meetingAccess) => {
                    if (!cancelled) setAccess(meetingAccess);
                })
                .catch((err: unknown) => {
                    if (cancelled) return;
                    setError(failureMessage(meetingJoinFailureFrom(err)));
                });

        // The booking's session first: it is what the call's tools save into —
        // EMDR ratings among them. The bare room is for when there is none to
        // be had yet.
        sessionForAppointment(appointmentId)
            .then(({ session_id }) => {
                if (!cancelled) navigate(`/meet/session/${session_id}`, { replace: true });
            })
            .catch(() => {
                if (!cancelled) void joinBareRoom();
            });

        return () => {
            cancelled = true;
        };
    }, [appointmentId, navigate, t]);

    // The same error surface the other two rooms use. This page used to render
    // its failure on the app's *light* background, because it is the one room
    // mounted inside the app shell — so the same class of failure looked like a
    // different product depending on which door you came in by.
    if (error) {
        return (
            <MeetingRoom>
                <MeetingHeader title={t("scheduler.meeting.title")} />
                <MeetingError
                    message={t(error)}
                    action={{
                        label: t("scheduler.meeting.backToScheduler"),
                        onClick: () => navigate("/scheduler"),
                    }}
                />
            </MeetingRoom>
        );
    }

    if (!access) {
        return (
            <MeetingRoom>
                <MeetingHeader title={t("scheduler.meeting.title")} />
                <div className="flex flex-1 items-center justify-center">
                    <Loader2
                        className="h-6 w-6 animate-spin text-room-ink-muted"
                        aria-hidden="true"
                    />
                    <span className="sr-only">{t("common.loading")}</span>
                </div>
            </MeetingRoom>
        );
    }

    return (
        <MeetingRoom>
            {/* This room had no header at all: a therapist opening it from
                their own calendar landed on a bare dark page with no branding,
                no context and no way out but hanging up. */}
            <MeetingHeader title={t("scheduler.meeting.title")}>
                <MeetingElapsed
                    room={appointmentId ? `appt-${appointmentId}` : null}
                    running={connected}
                />
            </MeetingHeader>
            <div
                className="mx-auto flex min-h-0 w-full max-w-5xl flex-1 flex-col p-2 sm:p-4 has-[[data-tool-board]]:max-w-none"
                data-testid="meeting-room"
            >
                <LiveKitRoom
                    data-lk-theme="default"
                    options={MEETING_ROOM_OPTIONS}
                    serverUrl={access.url}
                    token={access.token}
                    connect={true}
                    video={true}
                    audio={true}
                    onConnected={() => setConnected(true)}
                    // Leaving the room returns to the scheduler rather than a
                    // dead page, whether the therapist hung up or the
                    // connection did.
                    onDisconnected={() => navigate("/scheduler")}
                    className="h-full"
                >
                    <MeetingStage />
                </LiveKitRoom>
            </div>
        </MeetingRoom>
    );
}
